import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View, SuccessMessage } from '@olxbr/design-system'
import { Button } from '@olxbr/ds-core'
import { Alertbox } from '@olxbr/ds-web'
import { MessageCircle, Whatsapp } from '@olxbr/ds-icons'
import AvatarIcon from '../../atoms/AvatarIcon'

import Form from '../../atoms/Form'
import {
  getMaskedValue as getMaskedPhone,
  removeMask as removePhoneMask,
  isLandline,
} from '../../../util/PhonePattern'
import PinCode from '../../molecules/PinCode'
import { ErrorAlertbox } from '../../atoms/ErrorAlertbox/ErrorAlertbox'
import { VerifiedAlertbox } from '../../atoms/VerifiedAlertbox'
import { formatedTime } from '../../../util/date'
import { ConfirmResendCodeModal } from '../ConfirmResendCodeModal/ConfirmResendCodeModal'
import { LoadingSpinner } from '../../molecules/LoadingSpinner/LoadingSpinner'
import storage from '../../../util/storage'
import { LOGIN_METHOD_KEY } from '../../../util/global.consts'
import { breakpoints } from '../../../util/medias'

const Content = styled(View)`
  display: flex;
  flex: 1;
  flex-direction: column;
`

const FormContent = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const StyledButton = styled(Button)`
  margin-top: var(--spacing-4);
  width: -webkit-fill-available;
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    margin: 0;
    width: 100%;
    margin-top: var(--spacing-4);
  }
`
const WhatsAppButton = styled(StyledButton)`
  margin-top: var(--spacing-4);
  --button-background-color: var(--color-feedback-success-100);
  --button-background-color-hover: var(--color-feedback-success-110);
  --button-border: var(--color-feedback-success-100);
  --button-border-color-hover: var(--color-feedback-success-100);
  --button-color-font: var(--color-neutral-70);
`
const Icon = styled.span`
  margin-right: var(--spacing-1);
`

const defaultErrors = {
  showGenericError: false,
  showRateLimitError: false,
  showPhoneInUseError: false,
  showMaxPhoneValidationError: false,
  showPhoneInBlackListError: false,
  showInvalidPinCodeError: false,
  showResendCodeError: false,
  showMaxAccountCheckAttempts: false,
}

const defaultRemainingAttempts = 4
export const SEND_MODES = {
  WHATSAPP: 'WHATSAPP',
  PHONE: 'PHONE',
  SMS: 'SMS',
}

export const getSendMode = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const sendMode = urlParams.get('sendMode')
  if (!sendMode) {
    return 'not_set'
  } else if (sendMode === SEND_MODES.PHONE) {
    // URL param "PHONE" is expected due to historical reasons.
    // But when sendMode is PHONE, we use "sms" (in lowercase) for tracking reasons
    return SEND_MODES.SMS.toLowerCase()
  }
  return sendMode
}

const CheckPinCodeTemplate = ({
  checkCode,
  sendPinCode,
  sendSMS,
  makeCall,
  onSmsReceived,
  onSmsTimeout,
  onResend,
  onSuccess,
  onPinCodeError,
  lockTime,
  phone,
  from,
  lurker,
  publicAccountId,
  modalMode,
  defaultSendMode,
}) => {
  const isPhone2FAContext = sendPinCode === sendSMS && sendSMS === makeCall

  const [pincodeLocktime, setPincodeLocktime] = useState(0)
  const [remainingAttempts, setRemainingAttempts] = useState(defaultRemainingAttempts)
  const [errors, setErrors] = useState(defaultErrors)
  const [modalOpen, setModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [customErrorMsg, setCustomErrorMsg] = useState('')
  const [showResendCodeSuccessMessage, setShowResendCodeSuccessMessage] = useState(false)
  const [maxAttemptsSeen, setMaxAttemptsSeen] = useState(0)
  const [currentSendMode, setCurrentSendMode] = useState(defaultSendMode)

  const currentResendAttempt = lockTime > 0 ? Math.floor(lockTime / 60) : 0

  useEffect(() => {
    if (currentResendAttempt > maxAttemptsSeen) {
      setMaxAttemptsSeen(currentResendAttempt)
    }
  }, [currentResendAttempt])

  const totalAttempts = Math.max(currentResendAttempt, maxAttemptsSeen)

  const getButtonDisplayLogic = useCallback(() => {
    const shouldShowAlternativeButtons = totalAttempts >= 2 && lockTime <= 0
    const oppositeMode =
      defaultSendMode === SEND_MODES.WHATSAPP ? SEND_MODES.PHONE : SEND_MODES.WHATSAPP

    return {
      shouldShowAlternativeButtons,
      shouldShowPhoneButton: shouldShowAlternativeButtons && oppositeMode === SEND_MODES.PHONE,
      shouldShowWhatsAppButton:
        shouldShowAlternativeButtons && oppositeMode === SEND_MODES.WHATSAPP,
    }
  }, [totalAttempts, lockTime, defaultSendMode])

  const { shouldShowPhoneButton, shouldShowWhatsAppButton } = getButtonDisplayLogic()

  useEffect(() => {
    if (onSmsReceived) {
      onSmsReceived(smsData => {
        lurker.viewMessageEvt('auto_sms_success')
        onPinCodeChange(smsData.pinCode, true)
      })
    }
    if (onSmsTimeout) {
      onSmsTimeout(() => {
        lurker.viewMessageEvt('auto_sms_error')
      })
    }
  }, [])

  const onMaxAccountCheckAttemptReached = locktime => {
    setPincodeLocktime(locktime)
  }

  const updateRemainingAttempts = remainingAttempts => {
    setRemainingAttempts(remainingAttempts)
  }

  const showError = (error, customMsg = '') => {
    setErrors({
      ...defaultErrors,
      [error]: true,
    })
    setIsSubmitting(false)
    setIsResending(false)
    setCustomErrorMsg(customMsg)
  }

  /**
   * Handles changes to the PIN code input, validates the code, and triggers the verification process.
   * If the code is valid, it submits the code for verification and manages submission state.
   * On success, calls the success handler; on error, logs the error event and calls the error handler.
   *
   * @param {string} pinCode - The entered PIN code.
   * @param {boolean} isValid - Indicates whether the entered PIN code is valid.
   */
  const onPinCodeChange = (pinCode, isValid) => {
    if (isValid) {
      const sendMode = getSendMode()
      lurker.clickEvt('submit', {
        type: sendMode,
        flow: 'check_pincode',
      })
      setIsSubmitting(true)
      checkCode(phone, pinCode)
        .then(onCheckPinCodeSuccess)
        .catch(error => {
          if (from) {
            const { response } = error

            lurker.viewMessageEvt(`${from}_error`, {
              accounts_status_code: response?.data?.message,
              accounts_error_message: response?.status || 'unknown',
            })
          }

          error.flow = 'check_pincode'
          error.sendMode = sendMode
          onPinCodeError(
            error,
            'error',
            pinCode,
            showError,
            onMaxAccountCheckAttemptReached,
            updateRemainingAttempts
          )
        })
        .finally(() => setIsSubmitting(false))
    }
  }

  const resendCode = type => {
    setIsResending(true)
    lurker.clickEvt('resend_button', {
      type,
    })
    if (isLandline(phone)) {
      makeCall(isPhone2FAContext ? SEND_MODES.PHONE : phone)
        .then(() => onResendCodeSuccess('make_call_success'))
        .catch(error => {
          error.flow = 'make_call'
          onPinCodeError(
            error,
            'make_call_error',
            '',
            showError,
            onMaxAccountCheckAttemptReached,
            updateRemainingAttempts
          )
        })
        .finally(() => {
          if (typeof onResend === 'function') {
            onResend()
          }
        })
    } else {
      if (type === SEND_MODES.WHATSAPP) {
        lurker.clickEvt('resend_whatsapp', {
          event_type: 'click',
          object_name: 'phone_2fa',
          type,
          public_account_id: publicAccountId,
        })
        sendPinCode(type, phone)
          .then(() => {
            lurker.viewMessageEvt('success', {
              event_type: 'message_view',
              object_name: 'phone_2fa',
              flow: `resend_${type.toLowerCase()}`,
              type,
              public_account_id: publicAccountId,
            })
            onResendCodeSuccess('resend_button')
          })
          .catch(error => {
            const errorType = type === SEND_MODES.WHATSAPP ? 'resend_wpp_error' : 'resend_sms_error'
            const flow = `resend_${type.toLowerCase()}`
            lurker.viewMessageEvt('error', {
              object_name: 'phone_2fa',
              event_type: 'message_view',
              accounts_reason: 'rate_limit_exceeded',
              accounts_status_code: error?.response?.status,
              accounts_error_message: error?.message || error?.response?.data || error || 'unknown',
              flow,
              type: type,
              public_account_id: publicAccountId,
            })

            error.pincode_method = type
            error.flow = flow
            onPinCodeError(
              error,
              errorType,
              '',
              showError,
              onMaxAccountCheckAttemptReached,
              updateRemainingAttempts
            )
          })
          .finally(() => {
            if (typeof onResend === 'function') {
              onResend()
            }
          })
      } else {
        lurker.clickEvt('resend_sms')
        sendSMS(isPhone2FAContext ? SEND_MODES.PHONE : phone)
          .then(response => {
            lurker.viewMessageEvt('success', {
              event_type: 'message_view',
              object_name: 'phone_2fa',
              flow: 'resend_sms',
              type: SEND_MODES.SMS.toLowerCase(),
            })
            onResendCodeSuccess('resend_sms_success')
          })
          .catch(error => {
            lurker.viewMessageEvt('error', {
              event_type: 'message_view',
              object_name: 'phone_2fa',
              flow: 'resend_sms',
              type: SEND_MODES.SMS.toLowerCase(),
              accounts_reason: 'rate_limit_exceeded',
              accounts_status_code: error?.response?.status,
              accounts_error_message: error?.message || error?.response?.data || error || 'unknown',
            })
            error.pincode_method = SEND_MODES.SMS
            error.flow = 'resend_sms'
            onPinCodeError(
              error,
              'resend_sms_error',
              '',
              showError,
              onMaxAccountCheckAttemptReached,
              updateRemainingAttempts
            )
          })
          .finally(() => {
            if (typeof onResend === 'function') {
              onResend()
            }
          })
      }
    }
  }

  const resendCodeByCall = () => {
    setIsResending(true)
    lurker.clickEvt('make_call')

    makeCall(isPhone2FAContext ? SEND_MODES.PHONE : phone)
      .then(() => onResendCodeSuccess('make_call_success'))
      .catch(error => {
        error.flow = 'make_call'
        onPinCodeError(
          error,
          'make_call_error',
          '',
          showError,
          onMaxAccountCheckAttemptReached,
          updateRemainingAttempts
        )
      })
      .finally(() => {
        if (typeof onResend === 'function') {
          onResend()
        }
      })
  }

  const closeResendCodeSuccessMessage = () => {
    setShowResendCodeSuccessMessage(false)
  }

  const onResendCodeSuccess = eventName => {
    lurker.viewMessageEvt(eventName, {
      type: getSendMode(),
    })

    setShowResendCodeSuccessMessage(true)
    setIsResending(false)
  }

  const onCheckPinCodeSuccess = data => {
    const { publicAccountId } = data
    const loginMethod = storage.get(LOGIN_METHOD_KEY)

    lurker.setPublicAccountId(publicAccountId)

    lurker.viewMessageEvt('success', {
      accountId: data.accountId,
      flow: Object.keys(loginMethod).length > 0 ? String(loginMethod) : 'check_pincode',
      type: getSendMode(),
      context: modalMode ? 'modal' : 'page',
      public_account_id: publicAccountId,
    })

    if (from) {
      lurker.viewMessageEvt(`${from}_success`, {
        accountId: data.accountId,
      })
    }

    setIsSubmitting(false)
    if (typeof onSuccess === 'function') {
      ;(async () => {
        const method = await loginMethod
        onSuccess(removePhoneMask(phone), publicAccountId, method)
      })()
    }

    storage.remove(LOGIN_METHOD_KEY)
  }

  const closeModal = event => {
    if (event) event.preventDefault()
    setModalOpen(false)
  }

  const onResendClick = () => {
    if (!modalMode) {
      setModalOpen(true)
    } else {
      onResendConfirm()
    }
  }

  const onResendConfirm = () => {
    if (isLandline(phone)) {
      resendCodeByCall()
    } else {
      resendCode(defaultSendMode)
    }

    !modalMode && closeModal()
  }

  const onSubmit = type => {
    const eventType = type === SEND_MODES.WHATSAPP ? 'send_whatsapp' : 'send_sms'
    const errorType = type === SEND_MODES.WHATSAPP ? 'send_whatsapp_error' : 'send_sms_error'
    const flow = 'send_pincode'
    lurker.clickEvt(eventType, {
      type,
      flow,
    })

    setIsSubmitting(true)
    setCurrentSendMode(type)
    sendPinCode(type)
      .then(() => {
        lurker.viewMessageEvt(`${eventType}_success`, {
          flow,
          type,
          event_type: 'message_view',
          object_name: 'phone_2fa',
          object_detail: 'success',
          public_account_id: publicAccountId,
        })
      })
      .catch(error => {
        lurker.viewMessageEvt(`${eventType}_error`, {
          accounts_reason: 'rate_limit_exceeded',
          accounts_status_code: error?.response?.status,
          accounts_error_message: error?.message || error?.response?.data || error || 'unknown',
          flow,
          type,
          event_type: 'message_view',
          object_name: 'phone_2fa',
          object_detail: 'error',
          public_account_id: publicAccountId,
        })

        error.pincode_method = type
        error.flow = flow
        onPinCodeError(
          error,
          errorType,
          '',
          showError,
          onMaxAccountCheckAttemptReached,
          updateRemainingAttempts
        )
      })
      .finally(() => {
        setIsSubmitting(false)
        if (typeof onResend === 'function') {
          onResend()
        }
      })
  }

  useEffect(() => {
    lurker.setPublicAccountId(publicAccountId)
    const sendMode = getSendMode()
    lurker.pageLoadEvt(null, { type: sendMode, flow: 'check_pincode' })
  }, [publicAccountId])

  const {
    showGenericError,
    showMaxPhoneValidationError,
    showPhoneInUseError,
    showRateLimitError,
    showPhoneInBlackListError,
    showInvalidPinCodeError,
    showResendCodeError,
    showMaxAccountCheckAttempts,
  } = errors
  const maskedPhone = getMaskedPhone(phone, false)
  const isWhatsAppMode = currentSendMode === SEND_MODES.WHATSAPP

  return (
    <Content>
      {isSubmitting ? (
        <LoadingSpinner label="Concluindo verificação" aria-label="Validando código" />
      ) : (
        <React.Fragment>
          <span className="typo-body-medium mb-4 inline">
            {`Digite o código enviado por ${isWhatsAppMode ? 'WhatsApp' : 'SMS'} para`}
            <span className="typo-body-medium font-semibold flex items-center gap-1 mt-2">
              <AvatarIcon /> {maskedPhone.pretty}
            </span>
          </span>
          {showGenericError && (
            <ErrorAlertbox showContactUs={false}>
              Não foi possível validar seu telefone. Por favor, tente novamente.
            </ErrorAlertbox>
          )}

          {showResendCodeError && (
            <ErrorAlertbox showContactUs={false}>
              Não foi possível reenviar o código. Por favor, tente novamente.
            </ErrorAlertbox>
          )}

          {showPhoneInUseError && (
            <ErrorAlertbox showContactUs={false}>
              Este número de telefone já está sendo usado na OLX.
            </ErrorAlertbox>
          )}

          {showPhoneInBlackListError && (
            <ErrorAlertbox showContactUs={false}>
              Este número não pode ser cadastrado novamente.
            </ErrorAlertbox>
          )}

          {showMaxPhoneValidationError && (
            <ErrorAlertbox showContactUs={false} title="Limite de verificações atingido">
              {customErrorMsg ||
                'Esse telefone já foi utilizado para verificar uma conta nos últimos 30 dias.'}
            </ErrorAlertbox>
          )}

          {showRateLimitError && (
            <ErrorAlertbox title="Telefone temporariamente bloqueado" showContactUs />
          )}

          {showMaxAccountCheckAttempts && (
            <ErrorAlertbox title="Limite diário de tentativas atingido" showContactUs={false}>
              Tente novamente em {formatedTime(pincodeLocktime)}.
            </ErrorAlertbox>
          )}
          {showInvalidPinCodeError && (
            <ErrorAlertbox title="Código incorreto" showContactUs={false}>
              Você tem mais{' '}
              {`${remainingAttempts} ${remainingAttempts === 1 ? 'tentativa' : 'tentativas'}`}. Ao
              esgotá-las, só poderá tentar de novo em 24h.
            </ErrorAlertbox>
          )}
          <FormContent method="POST" noValidate>
            <PinCode
              disabled={isSubmitting || remainingAttempts === 0}
              onChange={(pincode, isValid) => {
                onPinCodeChange(pincode, isValid)

                if (showInvalidPinCodeError) {
                  setErrors(errors => ({ ...errors, showInvalidPinCodeError: false }))
                }
              }}
              hasError={showInvalidPinCodeError}
            />
          </FormContent>

          {shouldShowPhoneButton && (
            <StyledButton
              size="medium"
              disabled={isSubmitting || isResending}
              type="button"
              role="button"
              variant="tertiary"
              aria-label="Enviar código por SMS"
              onClick={() => {
                onSubmit(SEND_MODES.PHONE)
              }}
            >
              <Icon>
                <MessageCircle size="24" />
              </Icon>
              Receber por SMS
            </StyledButton>
          )}

          {shouldShowWhatsAppButton && (
            <WhatsAppButton
              size="medium"
              disabled={isSubmitting || isResending}
              type="button"
              role="button"
              aria-label="Enviar código por WhatsApp"
              onClick={() => {
                onSubmit(SEND_MODES.WHATSAPP)
              }}
            >
              <Icon>
                <Whatsapp />
              </Icon>
              Receber por WhatsApp
            </WhatsAppButton>
          )}

          <div className="flex items-center mt-4 mb-3">
            {lockTime <= 0 && remainingAttempts > 0 && (
              <span className="typo-body-small">Não recebeu?</span>
            )}
            <Button
              aria-label="Reenviar código"
              disabled={lockTime > 0 || isResending || remainingAttempts === 0}
              onClick={onResendClick}
              className="ml-0-5"
              size="small"
              variant="link"
            >
              {lockTime > 0 ? `Reenviar código em ${formatedTime(lockTime)}` : 'Reenviar código'}
            </Button>
          </div>
          <Alertbox variant="default" marginTop="--spacing-4">
            O código é privado. Não compartilhe essa informação com ninguém.
          </Alertbox>
          {isWhatsAppMode && (
            <VerifiedAlertbox>
              <b>Fique atento e confira o selo de verificação!</b> O código é enviado apenas pelo
              WhatsApp oficial da OLX.
            </VerifiedAlertbox>
          )}

          <SuccessMessage
            text="Código reenviado com sucesso"
            show={showResendCodeSuccessMessage}
            onClose={closeResendCodeSuccessMessage}
          />
          <ConfirmResendCodeModal
            phone={phone}
            onConfirm={onResendConfirm}
            closeModal={closeModal}
            isSubmitting={isResending}
            isOpen={modalOpen}
            defaultSendMode={defaultSendMode}
          />
        </React.Fragment>
      )}
    </Content>
  )
}

export { CheckPinCodeTemplate }

CheckPinCodeTemplate.propTypes = {
  checkCode: PropTypes.func.isRequired,
  sendPinCode: PropTypes.func.isRequired,
  sendSMS: PropTypes.func.isRequired,
  makeCall: PropTypes.func.isRequired,
  onSmsReceived: PropTypes.func,
  onSmsTimeout: PropTypes.func,
  onResend: PropTypes.func,
  onSuccess: PropTypes.func,
  onPinCodeError: PropTypes.func,
  lockTime: PropTypes.number,
  phone: PropTypes.string,
  from: PropTypes.string,
  lurker: PropTypes.object,
  publicAccountId: PropTypes.string,
  modalMode: PropTypes.bool,
  defaultSendMode: PropTypes.string,
}
